import AppService from "@service/AppService/AppService";
import { getTranslatedTextIfNeeded } from "@shared/InternationalizationTextUtils";
import { surveyQuestionMethods } from "@shared/Questions/Questions";

/** Plain string or localized payload from builder/API */
function comparableText(v) {
    if (v == null) {
        return "";
    }
    if (typeof v === "string") {
        return String(getTranslatedTextIfNeeded(v)).trim();
    }
    if (typeof v === "object" && v !== null) {
        if (v.EU != null) {
            return String(v.EU).trim();
        }
        if (v.ESP != null) {
            return String(v.ESP).trim();
        }
    }
    return String(v).trim();
}

export const MILK_DONATION_CAMPAIGN_YEAR_FIELD = "Campaign_Year__c";

const CAMPAIGN_YEAR_SAVE_MAX_ATTEMPTS = 3;
const CAMPAIGN_YEAR_SAVE_RETRY_DELAY_MS = 400;

function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

export function getMilkDonationCampaignYearCalendarFallback() {
    return String(new Date().getFullYear());
}

async function tryPersistCampaignYearAnswer(topic, question, yearValue) {
    const answer = prepareMilkDonationCampaignYearAnswer(question, yearValue);
    if (!answer) {
        return false;
    }
    surveyQuestionMethods.setLastAnswerId(question, answer);
    for (let attempt = 1; attempt <= CAMPAIGN_YEAR_SAVE_MAX_ATTEMPTS; attempt++) {
        try {
            await surveyQuestionMethods.setAnswer(topic, question, answer);
            return true;
        } catch {
            if (attempt < CAMPAIGN_YEAR_SAVE_MAX_ATTEMPTS) {
                await sleep(CAMPAIGN_YEAR_SAVE_RETRY_DELAY_MS * attempt);
            }
        }
    }
    return false;
}

export function isMilkDonationCampaignYearSfQuestion(question) {
    if (question?.salesForceField?.fieldName === MILK_DONATION_CAMPAIGN_YEAR_FIELD) {
        return true;
    }
    return question?.answers?.some((a) => a.salesForceField?.fieldName === MILK_DONATION_CAMPAIGN_YEAR_FIELD) ?? false;
}

export function forEachLeafQuestionInSection(section, visit) {
    if (!section?.questions?.length) {
        return;
    }
    section.questions.forEach((q) => {
        if (q.type === "GROUP") {
            q.questions?.forEach((gq) => visit(gq, section));
        } else if (q.type !== "NOTE") {
            visit(q, section);
        }
    });
}

export function findMilkDonationCampaignYearQuestion(section) {
    let found = null;
    forEachLeafQuestionInSection(section, (q) => {
        if (isMilkDonationCampaignYearSfQuestion(q)) {
            found = q;
        }
    });
    return found;
}

function normalizeYearValue(v) {
    return comparableText(v);
}

export function resolveMilkDonationCampaignYearValue(question) {
    if (!question) {
        return "";
    }
    const listName = surveyQuestionMethods.lastAnswerListName();
    const fromLast = normalizeYearValue(question[listName]?.[0]);
    if (fromLast) {
        return fromLast;
    }

    const row = question.answers?.[0];
    const fromUa = normalizeYearValue(row?.userAnswer);
    if (fromUa) {
        return fromUa;
    }

    return normalizeYearValue(row?.text);
}

export function getMilkDonationCampaignYearPersistedNormalized(question) {
    if (!question?.answers?.length) {
        return "";
    }

    const row = question.answers.find((a) => a.salesForceField?.fieldName === MILK_DONATION_CAMPAIGN_YEAR_FIELD) || question.answers[0];
    return normalizeYearValue(row?.userAnswer);
}

function campaignYearTargetAnswerRow(question) {
    if (!question?.answers?.length) {
        return null;
    }
    return (
        question.answers.find((a) => a.salesForceField?.fieldName === MILK_DONATION_CAMPAIGN_YEAR_FIELD) ||
        question.answers.find((a) => a.salesForceField?.id) ||
        question.answers[0]
    );
}

export function prepareMilkDonationCampaignYearAnswer(question, resolved) {
    const value = normalizeYearValue(resolved);
    if (!value || !question?.answers?.length) {
        return null;
    }

    const row = campaignYearTargetAnswerRow(question);
    if (!row) {
        return null;
    }
    row.userAnswer = value;
    return row;
}

function filterOutCampaignYearQuestions(questions) {
    return questions.flatMap((q) => {
        if (isMilkDonationCampaignYearSfQuestion(q)) {
            return [];
        }
        if (q.type === "GROUP") {
            return [{ ...q, questions: filterOutCampaignYearQuestions(q.questions || []) }];
        }
        return [q];
    });
}

export function getMilkDonorSurveySectionWithoutCampaignYear(section) {
    if (!section || AppService.accountType !== "milk_donor") {
        return section;
    }
    return {
        ...section,
        questions: filterOutCampaignYearQuestions(section.questions || [])
    };
}

export async function syncMilkDonationCampaignYearIfNeeded(topic) {
    if (AppService.accountType !== "milk_donor" || !topic) {
        return;
    }

    const question = findMilkDonationCampaignYearQuestion(topic);
    if (!question) {
        return;
    }

    const resolved = resolveMilkDonationCampaignYearValue(question);
    if (!resolved) {
        return;
    }

    const persisted = getMilkDonationCampaignYearPersistedNormalized(question);
    if (persisted === normalizeYearValue(resolved)) {
        return;
    }

    (await tryPersistCampaignYearAnswer(topic, question, resolved)) ||
        (await tryPersistCampaignYearAnswer(topic, question, getMilkDonationCampaignYearCalendarFallback()));
}
